import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faDisplay, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import Dropdown from '../../../../components/common/Dropdown.tsx';
import { Tooltip } from '@mui/material';
import IS_EFAS from '../../../../components/sitebase/Environment.ts';
import { FeatureWindowPresentation } from '../../Map.types';
import { WMSListPresentation } from '../wmslist/WMSList.types';
import { MapWidgetPosition } from './MapWidget.types';
import { useOutletContext } from 'react-router-dom';
import { ActiveLayersOpacity, ActiveLayersReordering, OutletIProps } from '../../../../components/sitebase/Base.types.tsx';
import { MapSettingsReturn } from '../../hooks/useMapSettings.ts';
import FeatureInfoPresentationSelector from '../FeatureInfoPresentationSelector';

type MapSettingsProps = {
    settings: MapSettingsReturn,
    activeNotificationWidgetPosition: MapWidgetPosition,
    setActiveNotificationWidgetPosition: (position: MapWidgetPosition) => void,
    wmsListPresentation: WMSListPresentation,
    onWmsListPresentationChange: (presentation: WMSListPresentation) => void,
    featureInfoPresentation: FeatureWindowPresentation,
    onFeatureInfoPresentationChange: (presentation: FeatureWindowPresentation) => void,
    checkForNewerForecast: () => Promise<void>,
};

export default function MapSettings(props: MapSettingsProps) {
    const context = useOutletContext<OutletIProps>();
    return (
        <Dropdown toggleButton={
            <Tooltip title="Map settings" placement="bottom" arrow disableInteractive>
                <a id="tutorial-map-settings" className="btn dropdown-toggle" role="button" aria-expanded="false">
                    <FontAwesomeIcon icon={faCog} />
                </a>
            </Tooltip>
        }
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            width="300px"
        >
            <div className='d-flex flex-column'>
                <Dropdown
                    toggleButton={
                        <button className='btn dropdown-toggle fs-7'>Base Map </button>
                    }
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    width="300px"
                >
                    <div className="form-check">
                        <div role="button">
                            <label className="form-check-label" htmlFor='baseMap1'>GISCO Open Street Map</label>
                            <input className="form-check-input" type="radio" name="basemap" id="baseMap1" defaultChecked={props.settings.selectedBaseMap === 1} onClick={() => { props.settings.setBaseMap(1) }} />
                        </div>
                        <div role="button">
                            <label className="form-check-label" htmlFor='baseMap2'>GISCO Continents</label>
                            <input className="form-check-input" type="radio" name="basemap" id="baseMap2" defaultChecked={props.settings.selectedBaseMap === 2} onClick={() => { props.settings.setBaseMap(2) }} />
                        </div>
                        <div role="button">
                            <label className="form-check-label" htmlFor='baseMap3'>GISCO Open Street Map Bright</label>
                            <input className="form-check-input" type="radio" name="basemap" id="baseMap3" defaultChecked={props.settings.selectedBaseMap === 3} onClick={() => { props.settings.setBaseMap(3) }} />
                        </div>
                        <div role="button">
                            <label className="form-check-label" htmlFor='baseMap4'>GISCO Open Street Map HDM</label>
                            <input className="form-check-input" type="radio" name="basemap" id="baseMap4" defaultChecked={props.settings.selectedBaseMap === 4} onClick={() => { props.settings.setBaseMap(4) }} />
                        </div>
                        <div role="button">
                            <label className="form-check-label" htmlFor='baseMap5'>MapTiler Topo</label>
                            <input className="form-check-input" type="radio" name="basemap" id="baseMap5" defaultChecked={props.settings.selectedBaseMap === 5} onClick={() => { props.settings.setBaseMap(5) }} />
                        </div>
                        <div role="button">
                            <label className="form-check-label" htmlFor='baseMap6'>MapTiler Satellite</label>
                            <input className="form-check-input" type="radio" name="basemap" id="baseMap6" defaultChecked={props.settings.selectedBaseMap === 6} onClick={() => { props.settings.setBaseMap(6) }} />
                        </div>
                        <div role="button">
                            <label className="form-check-label" htmlFor='baseMap7'>Grey Map</label>
                            <input className="form-check-input" type="radio" name="basemap" id="baseMap7" defaultChecked={props.settings.selectedBaseMap === 7} onClick={() => { props.settings.setBaseMap(7) }} />
                        </div>
                        <div role="button">
                            <label className="form-check-label" htmlFor='baseMap8'>Remove background layer</label>
                            <input className="form-check-input" type="radio" name="basemap" id="baseMap8" defaultChecked={props.settings.selectedBaseMap === 8} onClick={() => { props.settings.setBaseMap(8) }} />
                        </div>

                        <div role="button" className="mt-2 pt-2 border-top d-flex flex-column">
                            <label className="form-check-label" htmlFor='basemap-opacity'>Base Map Opacity</label>
                            <div className='d-flex flex-row'>
                                <input
                                    className='range flex-grow-1'
                                    id='basemap-opacity'
                                    type='range'
                                    min={0}
                                    max={1}
                                    step={0.1}
                                    value={props.settings.baseMapOpacity}
                                    onChange={(e) => {
                                        const value = parseFloat(e.target.value);
                                        props.settings.setBaseMapOpacity(value);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </Dropdown>
                <Dropdown
                    toggleButton={
                        <button className='btn dropdown-toggle fs-7'>Active Layers Position</button>
                    }
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    width="250px"
                >
                    <div className="form-check">
                        <div role="button">
                            <label className="form-check-label" htmlFor='forecastWidgetPosition-right'>Right</label>
                            <input className="form-check-input" type="radio" name="forecastWidgetPosition" id="forecastWidgetPosition-right"
                                defaultChecked={props.settings.forecastWidgetPosition === MapWidgetPosition.RIGHT} onClick={() => { props.settings.setForecastWidgetPosition(MapWidgetPosition.RIGHT) }} />
                        </div>
                        <div role="button">
                            <label className="form-check-label" htmlFor='forecastWidgetPosition-left'>Left</label>
                            <input className="form-check-input" type="radio" name="forecastWidgetPosition" id="forecastWidgetPosition-left"
                                defaultChecked={props.settings.forecastWidgetPosition === MapWidgetPosition.LEFT} onClick={() => { props.settings.setForecastWidgetPosition(MapWidgetPosition.LEFT) }} />
                        </div>
                        <div role="button">
                            <label className="form-check-label" htmlFor='forecastWidgetPosition-hidden'>Hidden</label>
                            <input className="form-check-input" type="radio" name="forecastWidgetPosition" id="forecastWidgetPosition-hidden"
                                defaultChecked={props.settings.forecastWidgetPosition === MapWidgetPosition.HIDDEN} onClick={() => { props.settings.setForecastWidgetPosition(MapWidgetPosition.HIDDEN) }} />
                        </div>
                    </div>
                </Dropdown>
                <Dropdown
                    toggleButton={
                        <button className='btn dropdown-toggle fs-7'>Active Layers Opacity</button>
                    }
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    width="250px"
                >
                    <div className="form-check">
                        <div>
                            <label className="form-check-label" htmlFor='activeLayersOpacity-dropdown'>Dropdown</label>
                            <input className="form-check-input" type="radio" name="activeLayersOpacity" id="activeLayersOpacity-dropdown"
                                defaultChecked={props.settings.activeLayersOpacity === ActiveLayersOpacity.DROPDOWN} onClick={() => { props.settings.setActiveLayersOpacity(ActiveLayersOpacity.DROPDOWN) }} />
                        </div>
                        <div>
                            <label className="form-check-label" htmlFor='activeLayersOpacity-visible'>Always Visible</label>
                            <input className="form-check-input" type="radio" name="activeLayersOpacity" id="activeLayersOpacity-visible"
                                defaultChecked={props.settings.activeLayersOpacity === ActiveLayersOpacity.ALWAYS_VISIBLE} onClick={() => { props.settings.setActiveLayersOpacity(ActiveLayersOpacity.ALWAYS_VISIBLE) }} />
                        </div>
                    </div>
                </Dropdown>
                <Dropdown
                    toggleButton={
                        <button className='btn dropdown-toggle fs-7'>Active Layers Reordering</button>
                    }
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    width="250px"
                >
                    <div className="form-check">
                        <div>
                            <label className="form-check-label" htmlFor='activeLayersReordering-arrows'>Arrows</label>
                            <input className="form-check-input" type="radio" name="activeLayersReordering" id="activeLayersReordering-arrows"
                                defaultChecked={props.settings.activeLayersReordering === ActiveLayersReordering.ARROWS} onClick={() => { props.settings.setActiveLayersReordering(ActiveLayersReordering.ARROWS) }} />
                        </div>
                        <div>
                            <label className="form-check-label" htmlFor='activeLayersReordering-dragging'>Dragging</label>
                            <input className="form-check-input" type="radio" name="activeLayersReordering" id="activeLayersReordering-dragging"
                                defaultChecked={props.settings.activeLayersReordering === ActiveLayersReordering.DRAGGING} onClick={() => { props.settings.setActiveLayersReordering(ActiveLayersReordering.DRAGGING) }} />
                        </div>
                    </div>
                </Dropdown>
                {IS_EFAS && <Dropdown
                    toggleButton={
                        <button className='btn dropdown-toggle fs-7'>Active notifications position</button>
                    }
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    width="250px"
                >
                    <div className="form-check">
                        <div>
                            <label className="form-check-label" htmlFor='activeNotificationsWidget-right'>Right</label>
                            <input className="form-check-input" type="radio" name="activeNotificationsWidget" id="activeNotificationsWidget-right"
                                defaultChecked={props.activeNotificationWidgetPosition === MapWidgetPosition.RIGHT} onClick={() => { props.setActiveNotificationWidgetPosition(MapWidgetPosition.RIGHT) }} />
                        </div>
                        <div>
                            <label className="form-check-label" htmlFor='activeNotificationsWidget-left'>Left</label>
                            <input className="form-check-input" type="radio" name="activeNotificationsWidget" id="activeNotificationsWidget-left"
                                defaultChecked={props.activeNotificationWidgetPosition === MapWidgetPosition.LEFT} onClick={() => { props.setActiveNotificationWidgetPosition(MapWidgetPosition.LEFT) }} />
                        </div>
                        <div>
                            <label className="form-check-label" htmlFor='activeNotificationsWidget-hidden'>Hidden</label>
                            <input className="form-check-input" type="radio" name="activeNotificationsWidget" id="activeNotificationsWidget-hidden"
                                defaultChecked={props.activeNotificationWidgetPosition === MapWidgetPosition.HIDDEN} onClick={() => { props.setActiveNotificationWidgetPosition(MapWidgetPosition.HIDDEN) }} />
                        </div>
                    </div>
                </Dropdown>}
                <Dropdown
                    toggleButton={
                        <button className='btn dropdown-toggle fs-7'>Layer Groups <FontAwesomeIcon icon={faDisplay} /></button>
                    }
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    width="250px"
                >
                    <div className="form-check">
                        <div>
                            <label className="form-check-label" htmlFor='wmsListPresentation-icons'>Icons</label>
                            <input className="form-check-input" type="radio" name="wmsListPresentation" id="wmsListPresentation-icons"
                                defaultChecked={props.wmsListPresentation === WMSListPresentation.ICONS} onClick={() => { props.onWmsListPresentationChange(WMSListPresentation.ICONS) }} />
                        </div>
                        <div>
                            <label className="form-check-label" htmlFor='wmsListPresentation-text'>Text</label>
                            <input className="form-check-input" type="radio" name="wmsListPresentation" id="wmsListPresentation-text"
                                defaultChecked={props.wmsListPresentation === WMSListPresentation.TEXT} onClick={() => { props.onWmsListPresentationChange(WMSListPresentation.TEXT) }} />
                        </div>
                    </div>
                </Dropdown>
                <Dropdown
                    toggleButton={
                        <button className='btn dropdown-toggle fs-7'>Point Information <FontAwesomeIcon icon={faDisplay} /></button>
                    }
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    width="250px"
                >
                    <FeatureInfoPresentationSelector
                        id="map-settings"
                        currentPresentation={props.featureInfoPresentation}
                        onPresentationChange={props.onFeatureInfoPresentationChange}
                    />
                </Dropdown>
                <Dropdown
                    toggleButton={
                        <button className='btn dropdown-toggle fs-7'>Map Markers <FontAwesomeIcon icon={faLocationDot} /></button>
                    }
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    width="250px"
                >
                    <div className="form-check">
                        <div>
                            <label className="form-check-label" htmlFor='useMapMarkers-one'>Show map markers for point information</label>
                            <input className="form-check-input" type="radio" name="useMapMarkers" id="useMapMarkers-one"
                                defaultChecked={props.settings.useMapMarkers === true} onClick={() => { props.settings.setUseMapMarkers(true) }} />
                        </div>
                        <div>
                            <label className="form-check-label" htmlFor='useMapMarkers-two'>Do not show any map markers</label>
                            <input className="form-check-input" type="radio" name="useMapMarkers" id="useMapMarkers-two"
                                defaultChecked={props.settings.useMapMarkers === false} onClick={() => { props.settings.setUseMapMarkers(false) }} />
                        </div>
                        <hr className='hr' />
                        <div>
                            <label className="form-check-label" htmlFor='useMapMarkers-three'>Remember map markers</label>
                            <input className="form-check-input" type="checkbox" name="useMapMarkers" id="useMapMarkers-three"
                                defaultChecked={props.settings.rememberMapMarkers} onClick={() => { props.settings.setRememberMapMarkers(!props.settings.rememberMapMarkers) }} />
                        </div>
                        <hr className='hr' />
                        <div>
                            <label className="form-check-label" htmlFor='closeRemovesPoint'>Closing a point window removes the point</label>
                            <input className="form-check-input" type="checkbox" name="closeRemovesPoint" id="closeRemovesPoint"
                                defaultChecked={props.settings.closeRemovesPoint} onClick={() => { props.settings.setCloseRemovesPoint(!props.settings.closeRemovesPoint) }} />
                        </div>
                    </div>
                </Dropdown>
                <button className='btn fs-7 text-start' onClick={() => props.settings.resetWidgetSizes()}>
                    Reset window sizes and positions
                </button>
                {context.user && context.user.email.includes("ecmwf") &&
                    <Dropdown
                        toggleButton={
                            <button className='btn dropdown-toggle fs-7'>Advanced</button>
                        }
                        width="250px"
                    >
                        <div className="form-check">
                            <div>
                                <label className="form-check-label" htmlFor='forceMapserver'>Force Mapserver</label>
                                <input className="form-check-input" type="checkbox" name="forceMapserver" id="forceMapserver"
                                    defaultChecked={props.settings.forceMapserver} onClick={() => { props.settings.setForceMapserver(!props.settings.forceMapserver) }} />
                            </div>
                            <div>
                                <label className="form-check-label" htmlFor='ignoreLatestForecastTime'>Ignore latest forecast time</label>
                                <input className="form-check-input" type="checkbox" name="ignoreLatestForecastTime" id="ignoreLatestForecastTime"
                                    defaultChecked={props.settings.ignoreLatestForecastTime} onClick={() => { props.settings.setIgnoreLatestForecastTime(!props.settings.ignoreLatestForecastTime) }} />
                            </div>
                            <hr className='hr' />
                            <button className='btn btn-sm btn-warning w-100' onClick={props.checkForNewerForecast}>
                                Check for newer forecast
                            </button>
                        </div>
                    </Dropdown>
                }
            </div>
        </Dropdown>
    )
}
